import type { ComponentType, ReactNode } from 'react';
import type { LucideProps } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface StatCardProps {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  icon?: ComponentType<LucideProps>;
  loading?: boolean;
  tone?: 'default' | 'primary' | 'secondary' | 'destructive';
  className?: string;
}

const toneClass: Record<NonNullable<StatCardProps['tone']>, string> = {
  default: 'bg-muted text-muted-foreground',
  primary: 'bg-primary/10 text-primary',
  secondary: 'bg-secondary/10 text-secondary',
  destructive: 'bg-destructive/10 text-destructive',
};

export function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  loading,
  tone = 'default',
  className,
}: StatCardProps) {
  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardContent className="flex items-center gap-3 p-4 sm:p-5">
        {Icon && (
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-md sm:h-12 sm:w-12',
              toneClass[tone],
            )}
          >
            <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
          </div>
        )}
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="truncate text-xs font-medium uppercase tracking-wide text-muted-foreground sm:text-sm">
            {label}
          </div>
          {loading ? (
            <Skeleton className="h-7 w-16" />
          ) : (
            <div className="truncate text-xl font-semibold leading-tight sm:text-2xl">
              {value}
            </div>
          )}
          {hint && (
            <div className="truncate text-xs text-muted-foreground">{hint}</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default StatCard;
